import * as React from 'react';
import * as PIXI from 'pixi.js-legacy';
import { MIPMAP_MODES } from 'pixi.js-legacy';
import RenderUtils from '../render_utils';
import FlatWorld from '../world';
import { IconButton } from '@material-ui/core';

import { COLORS } from 'src/utils/cubiButtonUtils';
import WorkspaceManager from 'src/workspace';
import small from 'src/assets/icons/resizeToSmall.svg';
import big from 'src/assets/icons/resizeToBig.svg';
import play from 'src/assets/icons/play.svg';
import restart from 'src/assets/icons/restart.svg';
import fullscreen from 'src/assets/icons/fullscreen.svg';
import exitFullscreen from 'src/assets/icons/exitfullscreen.svg';
import gamepad from 'src/assets/icons/gamepad.svg';
import spriteList from 'src/assets/icons/spriteList.svg';


const tvBackground = `
  <svg width="1057" height="717" viewBox="0 0 1057 717" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="126" y="656" width="159" height="61" rx="20" fill="#373737"/>
    <rect x="804" y="656" width="159" height="61" rx="20" fill="#373737"/>
    <rect x="4" y="4" width="1049" height="665" rx="26" fill="#636365" stroke="#373737" stroke-width="8"/>
  </svg>
`

interface PixiAppProps {
    readonly world: FlatWorld;
    readonly isPlaying: boolean;
    readonly runProject: () => void;
    readonly pauseProject: () => void;
    readonly resetProject: () => void;
    readonly setSize: (width: number) => void;
    readonly toggleGamepad: (on: boolean) => void;
    readonly gamepad: boolean;
    readonly isFullscreen: boolean;
    readonly showGamepad: boolean;
    readonly toggleFullscreen: () => void;
    readonly workspaceManager: WorkspaceManager;
}

interface PixiAppState {
    isPlaying: boolean;
    small: boolean;
}

export default class PixiApp extends React.Component<PixiAppProps, PixiAppState> {
    private app: PIXI.Application;
    private world: FlatWorld;
    private pixiDiv: React.RefObject<HTMLDivElement>;
    private buttonsContainerRef: React.RefObject<HTMLDivElement>;
    public ratio: number;
    public scaleChanged: boolean;

    constructor(props: PixiAppProps) {
        super(props);
        PIXI.settings.MIPMAP_TEXTURES = MIPMAP_MODES.ON;
        this.world = props.world;
        this.app = new PIXI.Application({antialias: false, resolution: window.devicePixelRatio || 1});
        this.app.renderer.view.style.borderRadius = '8px';
        this.app.renderer.backgroundColor = 0xffffff;
        // this.app.renderer.view.style.marginRight = '10px';
        this.app.renderer.view.style.backgroundColor = 'transparent';
        this.world.registerContainers(this.app.stage);
        this.pixiDiv = React.createRef();
        this.buttonsContainerRef = React.createRef();
        this.ratio = window.devicePixelRatio;
        this.scaleChanged = false;
        this.state = {
            isPlaying: props.isPlaying,
            small: true
        };
    }

    componentDidMount() {
        this.pixiDiv.current!.appendChild(this.app.view);
        window.addEventListener("resize", this.windowResized);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.windowResized);
    }

    windowResized = () => {
        const fullscreenWidth = Math.min(window.innerHeight * 4 / 6, this.pixiDiv.current!.getBoundingClientRect().width/2);
        const appWidth = this.props.isFullscreen ? fullscreenWidth : this.pixiDiv.current!.getBoundingClientRect().width;
        let renderUtils = (window as any).renderUtils as RenderUtils;
        if (this.scaleChanged) {
            const oldScale = this.world.scale;
            this.world.setScale(1 / window.devicePixelRatio);
            const factor = this.world.scale / oldScale;
            this.world.container.position.x *= factor;
            this.world.container.position.y *= factor;
            this.scaleChanged = false;
        }
        renderUtils.setResolution(appWidth / FlatWorld.worldSize().width / this.world.scale);
        };

    getRenderer() {
        return this.app.renderer;
    }
    
    togglePlayState = () => {
        this.setState(prevState => {
            const isPlaying = !prevState.isPlaying;
            if (isPlaying) {
                this.props.runProject();
                this.props.toggleGamepad(true);
            } else {
                this.props.resetProject();
                this.props.toggleGamepad(false);

            }
            return { isPlaying };
        });
    }

    handleResize = (size: number) => {
        window.dispatchEvent(new Event('resize'));
        this.props.setSize(size)
        this.windowResized();
    }

    handleFulsscreen = () => {
        this.props.toggleFullscreen();
        window.dispatchEvent(new Event('resize'));
        this.windowResized();
    }

    // Add this new public method to simulate button click
    public triggerResize() {
        const newSize = this.state.small ? 45 : 35;
        this.setState({small: !this.state.small});
        this.handleResize(newSize);
    }

    render() {
        return (
            <div style={ this.props.isFullscreen ? 
                {
                    backgroundImage: `url('data:image/svg+xml;charset=utf8,${encodeURIComponent(tvBackground)}')`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center', 
                    padding: '20px',
                } :
                { backgroundColor: '#97bce8', borderRadius: "8px", border: "3px solid rgb(55, 55, 55)", padding:'5px'}}>
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'  }} id={"pixi-app-container"} ref={this.pixiDiv}/>
                <div 
                    ref={this.buttonsContainerRef}
                    style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        justifyContent: 'center', 
                        alignItems: 'center',
                        marginTop: '10px', 
                        marginBottom: this.props.isFullscreen ? '40px' : '0px', 
                        gap: '10px',
                        rowGap: '10px'
                    }}>
                {
                <IconButton
                    style={{ backgroundColor: COLORS.RED, border: '3px solid rgb(55, 55, 55)', cursor: 'pointer', width: "60px", height: "60px", margin: 0, padding: "5px" }}
                    onClick={this.togglePlayState}>

                    <img src={this.state.isPlaying ? restart : play} alt="test"/>
                </IconButton>
                }
                {
                    !this.props.isFullscreen &&
                    <IconButton
                    style={{ backgroundColor: COLORS.ORANGE, border: '3px solid rgb(55, 55, 55)', cursor: 'pointer', width: "60px", height: "60px", margin: 0, padding: 0 }}
                    onClick={() => {
                        const newSize = this.state.small ? 45 : 35;
                        this.setState({small: !this.state.small});
                        this.handleResize(newSize);
                    }}>
                    <img src={this.state.small ? big : small} alt="test"/>
                </IconButton>}
                <IconButton
                    style={{ backgroundColor: COLORS.GREEN, border: '3px solid rgb(55, 55, 55)', cursor: 'pointer', width: "60px", height: "60px", margin: 0, padding: 0 }}
                    onClick={() => this.handleFulsscreen()}>
                    <img src={this.props.isFullscreen ? exitFullscreen : fullscreen} alt="test"/>
                </IconButton>
                { !this.props.isFullscreen && this.props.showGamepad &&
                    <IconButton
                    style={{ backgroundColor: COLORS.STEEL_BLUE, border: '3px solid rgb(55, 55, 55)', cursor: 'pointer', width: "60px", height: "60px", margin: 0, padding: 0 }}
                    onClick={() => this.props.toggleGamepad(!this.props.gamepad)}>
                    <img src={ this.props.gamepad ? spriteList : gamepad} alt="test"/>
                </IconButton>}
                </div>

            </div>
        );
    }
}
